import { useCustomViewBlockContext } from '@lighthouse/block'
import {
    type FieldADTValue,
    type FilterBlockIdentify,
    type FilterBlockItemConfig,
    type FilterBlockItemValue,
    FilterWay,
    getFilterBlockItemDefaultValue,
    ImageVariableValue,
    matchFieldPI,
    type RecordLikeProtocol,
    type SelectDataSourceVariable,
    type VariableADTvalue,
    VariableType
} from '@lighthouse/core'
import {
    applyBeijingVariableFormat,
    BEIJING_TIME_ZONE,
    type ComplexDataOfVariableAndJSONContentData,
    type DataOfVariableAndJSONContent,
    DATE_FORMAT,
    defaultFormat,
    evaluateFormulaResult,
    FilterWayToInnerTypeMap,
    findBlockById,
    getArgsResult,
    getBlockValueByContext,
    getDeviceId,
    getPagePathParams,
    getPageQueryParams,
    getRecordResult,
    getViewCustomStatisticConfig,
    getWithScopeId,
    getZonedDateValue,
    innerTypeToFieldType,
    inputTypeToFieldTypeMap,
    inputTypeToInnerTypeMap,
    isBeijingTimeZone,
    type ParseVariableOptions,
    readBeijingBaseFormat,
    recTakeFilterVariable,
    type ResolvedFilterExtraParams,
    resolveFilter,
    resolveViewVariableField,
    useAtomAction,
    USER_DATASOURCE
} from '@lighthouse/shared'
import type { JSONContent } from '@tiptap/core'
import equal from 'fast-deep-equal'
import type { Getter } from 'jotai'
import { atom, useAtomValue } from 'jotai'
import { selectAtom, useAtomCallback } from 'jotai/utils'
import { find, isEmpty, uniq } from 'rambda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { pageStackOfBlockAtom, syncComponentsAtom } from '@/atoms/application/state'
import { getRecordByFilterAtom } from '@/atoms/dataSource/action'
import { dataSourceAtomFamily, userRecordAtom } from '@/atoms/dataSource/state'
import {
    blocksAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStackAtomFamily,
    pageStateAtomFamily,
    pageStatesParameterFamily,
    pageVariableValueFamily
} from '@/atoms/page/state'
import { filterBlockCacheAtom } from '@/atoms/storage/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'

import { useCurrentAppId, useCurrentEnvId } from './useApplication'
import { usePageURI } from './usePageURI'

function takeVariable(node: JSONContent, list: VariableADTvalue[]): VariableADTvalue[] {
    if (node.type === 'variable' && node.attrs) {
        const value = node.attrs.value as VariableADTvalue
        const next = [...list, value]
        if (value.type === VariableType.FORMULA && value.formulaVariable?.value) {
            return takeVariable(value.formulaVariable.value, next)
        }
        return next
    }

    if (node.content) {
        const res = node.content.reduce<VariableADTvalue[]>((p, c) => [...p, ...takeVariable(c, [])], [])
        return [...list, ...res]
    }

    return []
}

type Params = {
    appId: string
    envId: string
    pageId: string
    stackId: string
    shareUrl: string
    viewRecord: RecordLikeProtocol | undefined
    option?: ParseVariableOptions
}

function getCurrentFilterItemValue(
    filterItem: FilterBlockItemConfig | undefined,
    itemValue?: FilterBlockItemValue,
    resolvedTextDefaultValue?: Pick<FieldADTValue, 'innerType' | 'value'>
) {
    if (!filterItem) {
        return
    }

    if (itemValue?.type === filterItem.filterWay) {
        return itemValue
    }

    return getFilterBlockItemDefaultValue(filterItem, undefined, resolvedTextDefaultValue)
}

/** 分析变量的可能依赖 */
function analyzingDependencies(get: Getter, variable: VariableADTvalue, params: Params): React.DependencyList | undefined {
    const { appId, envId, pageId, stackId, shareUrl, viewRecord, option } = params

    return matchFieldPI(variable)(
        {
            [VariableType.FORMULA]: v => {
                const doc = v.formulaVariable?.value
                if (!doc || isEmpty(doc)) {
                    return [void 0]
                }
                const variableList = takeVariable(doc, [])

                return variableList.reduce<React.DependencyList>((p, c) => [...p, ...(analyzingDependencies(get, c, params) || [])], [])
            },
            [VariableType.FORM]: v => {
                const fieldId = v.formVariable?.fieldId
                if (!fieldId) {
                    return [void 0]
                }
                const formRecord = option?.formRecord
                const ds = formRecord && get(dataSourceAtomFamily({ appId, envId, dsId: formRecord.dsId }))
                return [ds?.schema?.[fieldId], formRecord?.content?.[fieldId]?.value]
            },
            [VariableType.INPUT]: v => {
                const { blockId = '', scope: variableScope } = v.inputVariable ?? {}
                const pageStackOfBlock = get(pageStackOfBlockAtom)
                const blockKey = getWithScopeId(blockId, variableScope)
                const blockValue =
                    getBlockValueByContext(pageStackOfBlock, {
                        stackId,
                        blockId: blockKey,
                        recordId: option?.viewRecord?.id || viewRecord?.id
                    }) ??
                    getBlockValueByContext(pageStackOfBlock, {
                        stackId,
                        blockId: blockKey
                    })
                if (!blockValue) {
                    return [void 0, void 0]
                }
                if (blockValue.blockType === 'field') {
                    const { value, dsId = '', fieldId } = blockValue
                    const ds = get(dataSourceAtomFamily({ appId, envId, dsId }))
                    const field = ds?.schema?.[fieldId]
                    return [field, value]
                }
                if (blockValue.blockType === 'onOff') {
                    return [blockValue.value]
                }
                return [blockValue.value]
            },
            [VariableType.FILTER]: v => {
                const { blockId, itemId, scope: variableScope } = v.filterVariable ?? {}
                if (!blockId || !itemId) {
                    return [void 0, void 0]
                }
                const filterBlockCache = get(filterBlockCacheAtom)
                const key = [appId, envId, pageId, option?.viewRecord?.id || viewRecord?.id, variableScope, blockId]
                    .filter(Boolean)
                    .join('-') as FilterBlockIdentify
                /* 处理时间筛选和数值筛选的  如果暂时不处理可以注释 */

                const blocks = variableScope ? get(syncComponentsAtom) : get(selectAtom(blocksAtom, s => s[pageId], equal)) || []
                const filterBlock = findBlockById(blockId, blocks)
                if (!filterBlock || filterBlock.type !== 'filter') {
                    return [void 0]
                }

                const filterItem = filterBlock?.config.filterItems.find(item => item.id === itemId)
                const currentItemValue = filterBlockCache[key]?.[itemId]
                if (
                    (!currentItemValue || currentItemValue.type !== filterItem?.filterWay) &&
                    filterItem?.filterWay === FilterWay.textFilter &&
                    filterItem.textDefaultVariables
                ) {
                    return analyzingDependencies(get, filterItem.textDefaultVariables, params)
                }

                const itemValue = getCurrentFilterItemValue(filterItem, currentItemValue)
                if (!filterItem || !itemValue) {
                    return [void 0]
                }
                if (filterItem.filterWay === FilterWay.numberFilter) {
                    const numbers = itemValue.value
                    // itemValue.customValue
                    const numberValue = numbers
                        .map(val => {
                            if (val === 'custom') {
                                return itemValue.customValue
                            }
                            return filterItem.numberOptions?.find(option => option.id === val)?.value
                        })
                        .filter(Boolean)
                    return [numberValue]
                }
                if (filterItem.filterWay === FilterWay.dateFilter) {
                    const dates = itemValue.value
                    const dateValue = dates
                        .map(val => {
                            if (val === 'custom') {
                                return itemValue.customValue
                            }
                            return val
                        })
                        .filter(Boolean)
                    return [dateValue]
                }
                /* 处理时间筛选和数值筛选的  如果暂时不处理可以注释 */
                return [itemValue.value]
            },
            [VariableType.PAGE_DATA_QUERY]: v => {
                const { id, itemId } = v.pageDataQueryVariable ?? {}
                if (!id || !itemId) {
                    return [void 0, void 0, void 0]
                }

                const info = get(pageVariableValueFamily({ stackId, pageId, id, itemId }))

                return [info?.ds.schema?.[info.fieldId], info?.record.content?.[info.fieldId]?.value, info?.fieldId]
            },
            [VariableType.PAGE_LINK]: () => [shareUrl],
            [VariableType.DEVICE_ID]: () => [getDeviceId()],
            [VariableType.PAGE_STATE]: v => {
                const id = v.pageStateVariable?.id
                const states = get(pageStateAtomFamily(stackId))
                const state = states.find(item => item.id === id)
                return [state?.value]
            },
            [VariableType.ROUTE_PATH]: v => {
                const id = v.routePathVariable?.id
                const pagePathParameter = get(pagePathParameterFamily(pageId))
                const schema = pagePathParameter?.find(item => item.id === id)
                if (!schema) {
                    return [undefined]
                }

                const stack = get(pageStackAtomFamily(stackId))
                const pagePathParameterValues = getPagePathParams(pagePathParameter, stack?.pagePathParameters || [])

                return [pagePathParameterValues?.[schema.id]]
            },
            [VariableType.ROUTE_QUERY]: v => {
                const id = v.routeQueryVariable?.id
                const pageQueryParameter = get(pageQueryParameterFamily(pageId))
                const schema = pageQueryParameter?.find(item => item.id === id)
                if (!schema) {
                    return [undefined]
                }

                const stack = get(pageStackAtomFamily(stackId))
                const pageQueryParameterValues = getPageQueryParams(pageQueryParameter, stack?.pageQueryParameters || {})

                return [pageQueryParameterValues?.[schema.id]]
            },
            [VariableType.USER]: v => {
                const fieldId = v.userVariable?.fieldId
                if (!fieldId) {
                    return [void 0, void 0]
                }
                const userDs = get(dataSourceAtomFamily({ appId, envId, dsId: USER_DATASOURCE }))
                const userRecord = get(userRecordAtom)
                return [userDs?.schema?.[fieldId], userRecord?.content?.[fieldId]?.value]
            },
            [VariableType.VALUE]: () => {
                const userRecord = get(userRecordAtom)
                return [userRecord?.content['ID']?.value]
            },
            [VariableType.VIEW]: v => {
                const fieldId = v.viewVariable?.fieldId
                if (!fieldId) {
                    return [void 0]
                }
                const record = option?.viewRecord || viewRecord
                const ds = record && get(dataSourceAtomFamily({ appId, envId, dsId: record.dsId }))
                return [ds?.schema?.[fieldId], record?.content?.[fieldId]?.value, record?.id]
            }
        },
        () => void 0
    )
}

/**
 * 解析变量
 * 传data的话会解析变量依赖，订阅状态更新
 * 不传的话则不会订阅
 *
 * @param {?ComplexDataOfVariableAndJSONContentData} [data]
 * @returns {*}
 */
export const useParseVariableWithSubscription = (
    data?: ComplexDataOfVariableAndJSONContentData,
    props?: { pageId: string; stackId: string }
) => {
    const { pageId: propPageId, stackId: propStackId } = props ?? {}
    const { pageId: contextPageId } = useCurrentPageContext()
    const contextStackId = useCurrentStackIdContext()
    const pageId = propPageId || contextPageId
    const stackId = propStackId || contextStackId

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { shareUrl } = usePageURI({ isCurrentPage: true })
    const { record: viewRecord, viewId } = useCustomViewBlockContext()

    const lastData = useRef(data)
    const [compareData, setCompareData] = useState(data)

    useEffect(() => {
        try {
            if (equal(lastData.current, data)) {
                return
            }
        } catch {
            // JSONContent 节点属性中可能含有名为 valueOf 的普通字段，导致 fast-deep-equal 误判抛出 TypeError
            // 捕获后视为不相等，安全触发更新
        }

        lastData.current = data
        setCompareData(data)
    }, [data])

    const deps = useAtomValue(
        useMemo(
            () =>
                atom(get => {
                    if (!compareData) {
                        return
                    }

                    const commonParams = { appId, envId, pageId, stackId, viewRecord, shareUrl }

                    function build(withVariableData: DataOfVariableAndJSONContent) {
                        if (withVariableData.type === 'doc') {
                            const variableList = takeVariable(withVariableData, [])

                            return variableList.reduce<React.DependencyList>(
                                (p, c) => [...p, ...(analyzingDependencies(get, c, commonParams) || [])],
                                []
                            )
                        }

                        return analyzingDependencies(get, withVariableData as VariableADTvalue, commonParams)
                    }

                    return Array.isArray(compareData) ? compareData.flatMap(build) : build(compareData)
                }),
            [appId, compareData, envId, pageId, shareUrl, stackId, viewRecord]
        )
    )

    const [updateCount, setUpdateCount] = useState(0)
    const lastDeps = useRef(deps)
    useEffect(() => {
        if (equal(lastDeps.current, deps)) {
            return
        }
        lastDeps.current = deps
        setUpdateCount(s => s + 1)
    }, [deps])

    return useAtomCallback<FieldADTValue | undefined, [VariableADTvalue | undefined, ParseVariableOptions?]>(
        useCallback(
            (get, set, value, option): FieldADTValue | undefined => {
                if (!value) {
                    return
                }
                const matcher = matchFieldPI(value)
                const handlers: Parameters<typeof matcher>[0] = {
                    [VariableType.UPSTREAM]: v => {
                        const nodeId = v.upstreamVariable?.nodeId
                        const fieldId = v.upstreamVariable?.fieldId
                        if (!nodeId || !fieldId) {
                            return
                        }
                        const record = getRecordResult(option?.upstreamNodesResult?.[nodeId])
                        if (!record) {
                            return
                        }
                        const cell = record.content?.[fieldId]
                        if (!cell) {
                            return
                        }
                        const ds = get(dataSourceAtomFamily({ appId, envId, dsId: record.dsId }))
                        const field = ds?.schema?.[fieldId]
                        if (!field) {
                            return
                        }
                        return { ...field, value: cell.value }
                    },
                    [VariableType.ARG_VARIABLE]: v => {
                        const nodeId = v.argVariable?.nodeId
                        const argId = v.argVariable?.argId
                        if (!nodeId || !argId) {
                            return
                        }
                        const args = getArgsResult(option?.upstreamNodesResult?.[nodeId])
                        const arg = args?.find(item => item.id === argId)
                        if (!arg) {
                            return
                        }
                        return {
                            id: '',
                            dsId: '',
                            name: '',
                            type: 'formula',
                            innerType: arg.innerType,
                            value: arg.value,
                            formula: { expression: '' }
                        }
                    },
                    [VariableType.FORMULA]: v => {
                        const doc = v.formulaVariable?.value
                        if (!doc || isEmpty(doc)) {
                            return {
                                id: '',
                                dsId: '',
                                name: '',
                                type: 'formula',
                                innerType: 'TEXT',
                                value: undefined,
                                formula: { expression: '' }
                            }
                        }
                        const r = evaluateFormulaResult({
                            doc,
                            resolveVariableToField: inner =>
                                inner ? (matchFieldPI(inner)(handlers, () => void 0) as FieldADTValue | undefined) : undefined
                        })
                        if (!r) {
                            return {
                                id: '',
                                dsId: '',
                                name: '',
                                type: 'formula',
                                innerType: 'NULL',
                                value: '{公式错误}',
                                formula: { expression: '' }
                            }
                        }
                        return {
                            id: '',
                            dsId: '',
                            name: '',
                            type: 'formula',
                            innerType: r.innerType,
                            value: r.value,
                            formula: { expression: '' }
                        }
                    },
                    [VariableType.FIELD_ID]: v => {
                        const fieldId = v.fieldIdVariable?.fieldId ?? ''
                        return { value: fieldId, type: 'text', id: '', dsId: '', name: '', innerType: 'TEXT' }
                    },
                    [VariableType.FORM]: v => {
                        const fieldId = v.formVariable?.fieldId
                        if (!fieldId) {
                            return
                        }

                        const formRecord = option?.formRecord
                        if (!formRecord) {
                            return
                        }
                        const ds = get(dataSourceAtomFamily({ appId, envId, dsId: formRecord.dsId }))
                        if (!ds) {
                            return
                        }
                        const cellValue = formRecord?.content?.[fieldId]?.value
                        const field = ds?.schema?.[fieldId]
                        return { ...field, value: cellValue }
                    },
                    [VariableType.INPUT]: v => {
                        const { blockId = '', scope: variableScope } = v.inputVariable ?? {}
                        const scope = variableScope ? option?.scope || variableScope : undefined
                        // const isInputInCustom = isCustomChildren(blockId, get(blocksAtom)[pageId] || [])
                        const pageStackOfBlock = get(pageStackOfBlockAtom)
                        const blockKey = getWithScopeId(blockId, scope)
                        // Prefer record-scoped values when viewRecord exists.
                        // Fallback to higher-level (non-record) cache when the block value is stored outside recordId scope.
                        const blockValue =
                            getBlockValueByContext(pageStackOfBlock, {
                                stackId,
                                blockId: blockKey,
                                recordId: option?.viewRecord?.id || viewRecord?.id
                            }) ??
                            getBlockValueByContext(pageStackOfBlock, {
                                stackId,
                                blockId: blockKey
                            })
                        if (!blockValue) {
                            return
                        }
                        if (blockValue.blockType === 'field') {
                            const { value, dsId = '', fieldId, type } = blockValue
                            const ds = get(dataSourceAtomFamily({ appId, envId, dsId }))
                            const field = ds?.schema?.[fieldId]
                            const finallyField = {
                                id: '',
                                type: inputTypeToFieldTypeMap[type],
                                innerType: inputTypeToInnerTypeMap[type],
                                ...(type === 'date' ? { date: { format: defaultFormat } } : {}),
                                ...field
                            }
                            return { value, ...finallyField }
                        }
                        if (blockValue.blockType === 'text') {
                            return { id: '', type: 'text', innerType: 'TEXT', value: blockValue.value }
                        }
                        if (blockValue.blockType === 'onOff') {
                            return { id: '', type: 'checkbox', innerType: 'BOOL', value: blockValue.value }
                        }
                        return
                    },
                    [VariableType.FILTER]: v => {
                        const { blockId = '', scope: variableScope, itemId = '' } = v.filterVariable ?? {}

                        // const pageStackOfBlock = get(pageStackOfBlockAtom)[stackId]
                        const filterBlockCache = get(filterBlockCacheAtom)
                        const key = [appId, envId, pageId, option?.viewRecord?.id || viewRecord?.id, variableScope, blockId]
                            .filter(Boolean)
                            .join('-') as FilterBlockIdentify
                        const blocks = variableScope ? get(syncComponentsAtom) : get(selectAtom(blocksAtom, s => s[pageId], equal)) || []
                        const filterBlock = findBlockById(blockId, blocks)
                        if (!filterBlock || filterBlock.type !== 'filter') {
                            return
                        }

                        const filterItem = filterBlock?.config.filterItems.find(item => item.id === itemId)
                        const currentItemValue = filterBlockCache[key]?.[itemId]
                        const resolvedTextDefaultValue =
                            filterItem?.filterWay === FilterWay.textFilter &&
                            filterItem.textDefaultVariables &&
                            (!currentItemValue || currentItemValue.type !== filterItem.filterWay)
                                ? (matchFieldPI(filterItem.textDefaultVariables)(handlers, () => void 0) as FieldADTValue | undefined)
                                : undefined
                        const item = getCurrentFilterItemValue(filterItem, currentItemValue, resolvedTextDefaultValue)
                        if (!filterItem || !item) {
                            return
                        }
                        const { value, customValue, type } = item
                        if (type !== filterItem.filterWay || value === undefined || value.length === 0) {
                            return
                        }
                        const innerType = FilterWayToInnerTypeMap[filterItem.filterWay]
                        if (filterItem.filterWay === FilterWay.numberFilter) {
                            const numberValue = value
                                .map(val => {
                                    if (val === 'custom') {
                                        return customValue?.join(',')
                                    }
                                    const option = filterItem.numberOptions?.find(opt => opt.id === val)
                                    return option?.value?.join(',')
                                })
                                .filter(Boolean) as string[]
                            const finallyField = {
                                id: '',
                                type: innerTypeToFieldType[innerType],
                                innerType
                            }
                            return { value: numberValue, ...finallyField }
                        }

                        if (filterItem.filterWay === FilterWay.dateFilter) {
                            const { dateVariables } = filterItem
                            const dateValue = value
                                .map(val => {
                                    if (val === 'custom') {
                                        return customValue?.join(',')
                                    }
                                    return dateVariables?.includes(val) ? val : undefined
                                })
                                .filter(Boolean) as string[]
                            const finallyField = {
                                id: '',
                                type: innerTypeToFieldType[innerType],
                                innerType
                            }
                            return { value: dateValue, ...finallyField }
                        }

                        if (filterItem.filterWay === FilterWay.selectFilter) {
                            const { customOptions, pointer, fieldPointer } = filterItem
                            if (!pointer || !fieldPointer) {
                                return
                            }
                            const ds = get(dataSourceAtomFamily({ appId, envId, dsId: pointer }))
                            const field = ds?.schema[fieldPointer]
                            if (field?.type !== 'select') {
                                return
                            }
                            const fieldLabels = new Set(field.select.options.map(opt => opt.label))
                            const selectValue = value.filter(val => customOptions?.includes(val) && fieldLabels.has(val))
                            const finallyField = {
                                id: '',
                                type: innerTypeToFieldType[innerType],
                                innerType
                            }
                            return { value: selectValue, ...finallyField }
                        }

                        if (filterItem.filterWay === FilterWay.boolFilter) {
                            const finallyValue = value?.[0] ? value?.[0] === 'true' : undefined
                            if (finallyValue === undefined) {
                                return
                            }
                            const finallyField = {
                                id: '',
                                type: innerTypeToFieldType[innerType],
                                innerType
                            }
                            return { value: finallyValue, ...finallyField }
                        }
                        const finallyValue = value
                        const finallyField = {
                            id: '',
                            type: innerTypeToFieldType[innerType],
                            innerType
                        }
                        return { value: finallyValue, ...finallyField }
                    },
                    [VariableType.PAGE_DATA_QUERY]: v => {
                        const id = v.pageDataQueryVariable?.id
                        const itemId = v.pageDataQueryVariable?.itemId
                        if (!id || !itemId) {
                            return
                        }
                        const info = get(pageVariableValueFamily({ stackId, pageId, id, itemId }))
                        if (!info) {
                            return
                        }
                        const { ds, record, fieldId } = info

                        const field = ds.schema[fieldId]
                        if (!field) {
                            return
                        }

                        return {
                            value: record.content[fieldId]?.value,
                            ...field,
                            ...(field.type === 'date'
                                ? { date: { format: applyBeijingVariableFormat(v.pageDataQueryVariable?.format, field.date.format || DATE_FORMAT) || field.date.format } }
                                : field.innerType === 'DATE'
                                ? { date: { format: applyBeijingVariableFormat(v.pageDataQueryVariable?.format, 'yyyy-MM-dd') } }
                                : {})
                        }
                    },
                    [VariableType.PAGE_LINK]: v => {
                        return { value: shareUrl, type: 'url', innerType: 'TEXT', id: '' }
                    },
                    [VariableType.DEVICE_ID]: () => {
                        return { value: getDeviceId(), type: 'text', innerType: 'TEXT', id: '' }
                    },
                    [VariableType.PAGE_STATE]: v => {
                        const id = v.pageStateVariable?.id
                        if (!id) {
                            return
                        }
                        const pageStateParameterValues = get(pageStatesParameterFamily(pageId))
                        if (!pageStateParameterValues) {
                            return
                        }
                        const pageStateParameterValue = find(item => item.id === id, pageStateParameterValues)
                        if (!pageStateParameterValue) {
                            return
                        }
                        const pageState = get(pageStateAtomFamily(stackId))
                        const pageStateValue = find(item => item.id === id, pageState)
                        if (!pageStateValue) {
                            return
                        }
                        return { ...pageStateValue, type: innerTypeToFieldType[pageStateParameterValue.innerType] }
                    },
                    [VariableType.RESOLVED]: v => {
                        return { ...v.resolvedVariable, id: '' }
                    },
                    [VariableType.ROUTE_PATH]: v => {
                        const id = v.routePathVariable?.id
                        if (!id) {
                            return
                        }

                        const pagePathParameter = get(pagePathParameterFamily(pageId))
                        const schema = pagePathParameter?.find(item => item.id === id)
                        if (!schema) {
                            return
                        }

                        const stack = get(pageStackAtomFamily(stackId))
                        const pagePathParameterValues = getPagePathParams(pagePathParameter, stack?.pagePathParameters || [])
                        if (!pagePathParameterValues) {
                            return
                        }

                        return {
                            value: pagePathParameterValues[schema.id],
                            type: innerTypeToFieldType[schema.innerType],
                            innerType: schema.innerType,
                            id: ''
                        }
                    },
                    [VariableType.ROUTE_QUERY]: v => {
                        const id = v.routeQueryVariable?.id
                        if (!id) {
                            return
                        }

                        const pageQueryParameter = get(pageQueryParameterFamily(pageId))
                        const schema = pageQueryParameter?.find(item => item.id === id)
                        if (!schema) {
                            return
                        }

                        const stack = get(pageStackAtomFamily(stackId))
                        const pageQueryParameterValues = getPageQueryParams(pageQueryParameter, stack?.pageQueryParameters || {})
                        if (!pageQueryParameterValues) {
                            return
                        }

                        return {
                            value: pageQueryParameterValues?.[schema.id],
                            type: innerTypeToFieldType[schema.innerType],
                            innerType: schema.innerType,
                            id: ''
                        }
                    },

                    [VariableType.SYSTEM]: v => {
                        const selected = v.systemVariable?.format
                        const timeZone =
                            isBeijingTimeZone(option?.timeZone) || readBeijingBaseFormat(selected || '') ? BEIJING_TIME_ZONE : undefined
                        const dateValues = v.systemVariable?.value
                            ? getZonedDateValue({ firstParam: v.systemVariable.value }, timeZone)
                            : undefined
                        return {
                            id: '',
                            type: 'date',
                            value: Array.isArray(dateValues) ? dateValues[0] : undefined,
                            date: { format: applyBeijingVariableFormat(selected, 'yyyy-MM-dd') || 'yyyy-MM-dd' },
                            innerType: 'DATE'
                        }
                    },
                    [VariableType.USER]: v => {
                        const fieldId = v.userVariable?.fieldId
                        if (!fieldId) {
                            return
                        }

                        const userDs = get(dataSourceAtomFamily({ appId, envId, dsId: USER_DATASOURCE }))
                        const userRecord = get(userRecordAtom)
                        if (!userDs || !userRecord) {
                            return
                        }

                        const field = userDs.schema[fieldId]
                        if (!field) {
                            return
                        }
                        return {
                            value: userRecord.content[fieldId]?.value,
                            ...field,
                            ...(field.type === 'date'
                                ? { date: { format: applyBeijingVariableFormat(v.userVariable?.format, field.date.format || DATE_FORMAT) || field.date.format } }
                                : field.innerType === 'DATE'
                                ? { date: { format: applyBeijingVariableFormat(v.userVariable?.format, 'yyyy-MM-dd') } }
                                : {})
                        }
                    },

                    [VariableType.VALUE]: v => {
                        const idVariableValue = v.valueVariable?.value

                        const userRecord = get(userRecordAtom)
                        const value = Array.isArray(idVariableValue)
                            ? uniq(idVariableValue.map(item => (item === '{currentUserId}' ? userRecord?.content['ID']?.value : item)))
                            : idVariableValue
                        return {
                            ...v.valueVariable,
                            value,
                            id: '',
                            innerType: 'RUNTIME'
                        }
                    },
                    [VariableType.VIEW]: v => {
                        const fieldId = v.viewVariable?.fieldId
                        const finalViewRecord = option?.viewRecord || viewRecord
                        if (!fieldId || !finalViewRecord) {
                            return
                        }
                        const viewDs = get(dataSourceAtomFamily({ appId, envId, dsId: finalViewRecord.dsId }))
                        const cellValue = finalViewRecord?.content?.[fieldId]?.value
                        const pageBlocks = get(blocksAtom)[pageId] || []
                        const syncBlocks = get(syncComponentsAtom)
                        const viewBlock = findBlockById(viewId, pageBlocks) || findBlockById(viewId, syncBlocks)
                        const field = resolveViewVariableField(
                            viewDs?.schema,
                            fieldId,
                            getViewCustomStatisticConfig(viewBlock?.type === 'view' ? viewBlock.config : undefined),
                            viewDs?.id
                        )
                        if (!field) {
                            return
                        }

                        return {
                            ...field,
                            value: cellValue,
                            ...(field.type === 'date'
                                ? { date: { format: applyBeijingVariableFormat(v.viewVariable?.format, field.date.format || DATE_FORMAT) || field.date.format } }
                                : field.innerType === 'DATE'
                                ? { date: { format: applyBeijingVariableFormat(v.viewVariable?.format, 'yyyy-MM-dd') } }
                                : {})
                        }
                    },
                    [VariableType.IMAGE]: v => {
                        const imageInfo = option?.imageInfo
                        if (!imageInfo) {
                            return
                        }
                        const dataKey = v.imageVariable?.dataKey
                        if (!dataKey) {
                            return
                        }
                        const data = imageInfo[dataKey]
                        if (!data) {
                            return
                        }
                        if (dataKey === ImageVariableValue.IMAGE_INDEX) {
                            return {
                                id: '',
                                type: 'number',
                                value: data,
                                innerType: 'NUMBER'
                            }
                        }
                        return {
                            id: '',
                            type: 'text',
                            value: data,
                            innerType: 'TEXT'
                        }
                    }
                }

                return matcher(handlers, () => void 0) as FieldADTValue | undefined
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [appId, envId, pageId, stackId, updateCount, viewId]
        )
    )
}

/**
 * 区分异步的，目前只有 selectDataSourceVariable
 * @returns {*}
 */
export const useParseVariable2Field4SelectDataSource = (data?: VariableADTvalue, props?: { pageId: string; stackId: string }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { pageId: propPageId, stackId: propStackId } = props ?? {}
    const { pageId: contextPageId } = useCurrentPageContext()
    const contextStackId = useCurrentStackIdContext()
    const pageId = propPageId || contextPageId
    const stackId = propStackId || contextStackId
    // const { pageId } = useCurrentPageContext()
    // const stackId = useCurrentStackIdContext()
    const { record: viewRecord } = useCustomViewBlockContext()
    const { run: getRecordByFilter } = useAtomAction(getRecordByFilterAtom)
    const { shareUrl } = usePageURI({ isCurrentPage: true })

    const selectDataSourceVariableFilter = useMemo(() => {
        if (!data) {
            return
        }
        if (data.type === VariableType.SELECT_DATASOURCE) {
            return data.selectDataSourceVariable?.filter
        }
        return
    }, [data])

    const lastData = useRef(selectDataSourceVariableFilter)
    const [compareData, setCompareData] = useState(selectDataSourceVariableFilter)

    useEffect(() => {
        if (equal(lastData.current, selectDataSourceVariableFilter)) {
            return
        }

        lastData.current = selectDataSourceVariableFilter
        setCompareData(selectDataSourceVariableFilter)
    }, [selectDataSourceVariableFilter])

    const deps = useAtomValue(
        useMemo(
            () =>
                atom(get => {
                    if (!compareData) {
                        return
                    }
                    const variableList = recTakeFilterVariable(compareData.expression)
                    if (!variableList?.length) {
                        return
                    }
                    const commonParams = { appId, envId, pageId, stackId, viewRecord, shareUrl }
                    return variableList.reduce<React.DependencyList>(
                        (p, c) => [...p, ...(analyzingDependencies(get, c, commonParams) || [])],
                        []
                    )
                }),
            [appId, compareData, envId, pageId, shareUrl, stackId, viewRecord]
        )
    )

    const [updateCount, setUpdateCount] = useState(0)
    const lastDeps = useRef(deps)
    useEffect(() => {
        if (equal(lastDeps.current, deps)) {
            return
        }
        lastDeps.current = deps
        setUpdateCount(s => s + 1)
    }, [deps])

    return useAtomCallback(
        useCallback(
            async (get, set, variable: SelectDataSourceVariable, extraParams: ResolvedFilterExtraParams) => {
                if (!variable.selectDataSourceVariable) {
                    return
                }
                const { dsId, fieldId, filter, sorts } = variable.selectDataSourceVariable
                const ds = get(dataSourceAtomFamily({ appId, envId, dsId }))
                if (!ds) {
                    return
                }
                const record = await getRecordByFilter({
                    appId,
                    envId,
                    dsId,
                    filter: resolveFilter({
                        filter,
                        extraParams
                    }),
                    sorts
                })
                return { value: record?.content?.[fieldId]?.value, ...ds.schema[fieldId] } as FieldADTValue
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [appId, envId, getRecordByFilter, updateCount]
        )
    )
}
